import React, { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { hotelService } from "@/services/hotel-service-service";
import {
  HotelServiceImportResult,
  HotelServiceProviderType,
} from "@/types/hotelService";
import { downloadBlob } from "@/utils/downloadFile";

interface ImportServicesModalProps {
  hotelId: string;
  providerType: HotelServiceProviderType;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TEMPLATE_HEADERS = [
  "Name",
  "Description",
  "Category",
  "Provider Type",
  "Price",
  "Discount",
  "Status",
  "Recommended",
  "Payment Enabled",
  "Payment Required",
  "Automatically Created",
  "Auto Create Triggers",
  "Check-in Available",
  "Check-out Available",
  "Available From",
  "Available To",
  "Available Days",
];

export const ImportServicesModal = ({
  hotelId,
  providerType,
  open,
  onClose,
  onSuccess,
}: ImportServicesModalProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<HotelServiceImportResult | null>(null);

  const close = () => {
    if (isImporting) return;
    setFile(null);
    setResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const example = [
      "Breakfast",
      "Breakfast buffet",
      "Existing category name",
      providerType,
      "25",
      "0",
      "ACTIVE",
      "false",
      "true",
      "true",
      "false",
      "",
      "false",
      "false",
      "07:00",
      "11:00",
      "Mon|Tue|Wed|Thu|Fri|Sat|Sun",
    ];
    const csv = `${TEMPLATE_HEADERS.join(",")}\n${example
      .map((value) => `"${value.replace(/"/g, '""')}"`)
      .join(",")}\n`;

    downloadBlob(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
      `hotel-services-${providerType.toLowerCase()}-template.csv`,
    );
  };

  const handleImport = async () => {
    if (!file) return;

    setIsImporting(true);
    setResult(null);
    try {
      const importResult = await hotelService.importServices(hotelId, file);
      setResult(importResult);

      if (importResult.imported > 0) {
        onSuccess();
      }
      if (importResult.failed === 0) {
        toast.success(
          t("services.page.import.success", "Services imported successfully"),
        );
        setFile(null);
        setResult(null);
        onClose();
      } else {
        toast.warning(
          t(
            "services.page.import.partial",
            "Import completed with some errors",
          ),
        );
      }
    } catch (error) {
      console.error("Service import failed", error);
      toast.error(
        t("services.page.import.failed", "Failed to import services"),
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && close()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {t("services.page.import.title", "Import services")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "services.page.import.description",
              "Upload a CSV file. Category names must already exist in this hotel.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="services-csv-file">
              {t("services.page.import.csvFile", "CSV file")}
            </Label>
            <Input
              id="services-csv-file"
              type="file"
              accept=".csv,text/csv"
              disabled={isImporting}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit gap-2"
            onClick={downloadTemplate}
          >
            <Download className="h-4 w-4" />
            {t("services.page.import.downloadTemplate", "Download template")}
          </Button>

          {result ? (
            <div className="bg-muted/50 space-y-2 rounded-lg border p-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                {t("services.page.import.imported", "Imported")}:{" "}
                {result.imported}
              </div>
              {result.failed > 0 ? (
                <>
                  <div className="text-destructive flex items-center gap-2 text-sm font-medium">
                    <AlertCircle className="h-4 w-4" />
                    {t("services.page.import.failedRows", "Failed")}:{" "}
                    {result.failed}
                  </div>
                  <div className="max-h-40 space-y-1 overflow-y-auto text-xs">
                    {result.errors.map((error) => (
                      <div
                        key={`${error.row}-${error.name ?? "service"}`}
                        className="text-destructive"
                      >
                        {t("services.page.import.row", "Row")} {error.row}
                        {error.name ? ` (${error.name})` : ""}: {error.error}
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={close}
            disabled={isImporting}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            className="gap-2"
            onClick={() => void handleImport()}
            disabled={!file || isImporting}
          >
            {isImporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {t("services.page.actions.import", "Import")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
